import { ChangeDetectionStrategy, Component, computed, Input, OnDestroy, signal } from '@angular/core';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { AsyncValidatorFn, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { filter, map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { ANIMATION_slideInOut } from '../../animations';
import { isPageBuilderDesignMode } from '../../misc/pagebuilder';
import { getGETParameters } from '../../misc/page-parameter';
import { GrowthBookFeature, GrowthBookService } from '../../services/growthbook.service';
import { toSignal } from '@angular/core/rxjs-interop';

export type DisplayStatus = 'collapsable' | 'visible';

@Component({
  selector: 'orderform-input-voucher',
  templateUrl: './input-voucher.component.html',
  styleUrls: ['./input-voucher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ANIMATION_slideInOut],
})
export class InputVoucherComponent implements OnDestroy {
  PGB_VIEW_MODE = isPageBuilderDesignMode();
  getParam = getGETParameters('voucher');
  isGetParamExists = !!this.getParam;

  private readonly destroy$ = new ReplaySubject<void>(1);

  readonly _displayStatus = signal<DisplayStatus>('collapsable');
  @Input() set displayStatus(state: DisplayStatus) {
    this._displayStatus.set(state);
  }

  private readonly isPlainModeEnabled = toSignal(
    this._orderformFascade.globalSettings$.pipe(map((setting) => setting.is_plain_mode_enabled)),
    { initialValue: false }
  );

  readonly openToggle = signal(this.isGetParamExists);

  isPlainModeEnabled$ = this._orderformFascade.globalSettings$.pipe(
    takeUntil(this.destroy$),
    map((setting) => setting.is_plain_mode_enabled)
  );

  readonly isOpen = computed(() => {
    const displayStatus = this._displayStatus();
    const isPlainModeEnabled = this.isPlainModeEnabled();
    const isAccesibilityEnabled = this.growthBookService.isFeatureEnabled(GrowthBookFeature.ACCESSIBILITY_TOGGLE);

    if (isAccesibilityEnabled && isPlainModeEnabled) {
      return true;
    }

    if (displayStatus === 'visible') {
      return true;
    }

    return this.openToggle();
  });

  showVisibleInfo$ = this._orderformFascade.globalSettings$.pipe(
    map((gSetting) => !gSetting.is_voucher_input_enabled && this.PGB_VIEW_MODE)
  );

  isEnabled$ = this._orderformFascade.globalSettings$.pipe(
    map((gSetting) => gSetting.is_voucher_input_enabled || this.isGetParamExists || this.PGB_VIEW_MODE)
  );

  isReadyOnly$ = this._orderformFascade.globalSettings$.pipe(map((gSetting) => !!gSetting.is_voucher_input_readonly));

  order$ = this._orderformFascade.order$;

  voucherCodeValidator(): AsyncValidatorFn {
    return (): Observable<ValidationErrors | null> => {
      return this._orderformFascade.requestPending$.pipe(
        switchMap(() =>
          this._orderformFascade.validation$.pipe(
            filter((v) => v.voucher_code !== '' || v.success_by_postname['voucher_code'] !== ''),
            map((v) => (v.voucher_code ? { invalid: true } : null))
          )
        ),
        take(1)
      );
    };
  }

  formGroup$ = this.order$.pipe(
    map((orderState) => {
      const voucherCode = orderState.order.voucher_code || this.getParam;

      if (voucherCode) {
        this.openToggle.set(true);
      }

      return new UntypedFormGroup({
        voucherCode: new UntypedFormControl(voucherCode, {
          asyncValidators: [this.voucherCodeValidator()],
        }),
      });
    }),
    shareReplay(1)
  );

  validate$ = this._orderformFascade.validation$.pipe(
    map((validation) => validation.voucher_code),
    shareReplay(1)
  );

  successMessage$ = this._orderformFascade.validation$.pipe(
    map((validations) => validations.success_by_postname['voucher_code'] || ''),
    shareReplay(1)
  );

  constructor(
    private readonly _orderformFascade: OrderformFascade,
    private readonly growthBookService: GrowthBookService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  apply(formGroup: UntypedFormGroup) {
    this._orderformFascade.setOrderStore({
      voucher_code: formGroup.get('voucherCode').value,
      voucher_code_added: true,
    });

    firstValueFrom(this._orderformFascade.update$());
    formGroup.get('voucherCode').updateValueAndValidity();
  }
}
